import { v4 as uuidv4 } from 'uuid'
import throttle from 'lodash/throttle'
import { defineStore } from 'pinia'
import { ref } from 'vue'

import { ToastEvent } from './types'
import type { ToastProps, ToastAction } from './types'

export type ToastLocation =
  | 'bottom-right'
  | 'bottom-left'
  | 'top-left'
  | 'top-right'
  | 'top-center'
  | 'center'
export type MountProps = {
  extended?: boolean // uses ExtendedToast.vue instead of MinimalToast.vue
  duration?: number
  actions?: ToastAction[]
} & Omit<ToastProps, 'variant'>

export const useToast = defineStore('toast', () => {
  const toasts = ref<(MountProps & { variant: ToastEvent; id: string })[]>([])

  const unmountInTime = (id: string, duration?: number): void => {
    setTimeout(() => {
      toasts.value = toasts.value.filter((toast) => toast.id !== id)
    }, duration || 3000) as unknown
  }

  const mountToast = throttle((args: MountProps & { variant: ToastEvent }): void => {
    const id = uuidv4()

    toasts.value.unshift({
      id,
      ...args,
    })

    unmountInTime(id, args.duration)
  }, 150)

  const error = (args: MountProps): void => {
    mountToast({
      variant: ToastEvent.ERROR,
      ...args,
    })
  }

  const warning = (args: MountProps): void => {
    mountToast({
      variant: ToastEvent.WARNING,
      ...args,
    })
  }

  const success = (args: MountProps): void => {
    mountToast({
      variant: ToastEvent.SUCCESS,
      ...args,
    })
  }

  const info = (args: MountProps): void => {
    mountToast({
      variant: ToastEvent.DEFAULT,
      ...args,
    })
  }

  const processing = (args: MountProps): void => {
    mountToast({
      variant: ToastEvent.PROCESSING,
      ...args,
    })
  }

  const dismiss = (id: string): void => {
    toasts.value = toasts.value.filter((toast) => toast.id !== id)
  }

  return { toasts, error, warning, success, info, processing, dismiss }
})
